import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import api from '../../services/api';
import { Link, useNavigate } from 'react-router-dom';
import DataGrid, {
  Column,
  Paging,
  Pager,
  FilterRow,
  Selection,
  SearchPanel,
  Toolbar,
  Item,
} from 'devextreme-react/data-grid';
import { ContextMenu } from 'devextreme-react/context-menu';
import 'devextreme/dist/css/dx.material.blue.light.compact.css';
import { LoadPanel } from 'devextreme-react/load-panel';

const FlifgtList = () => {
  const [applicants, setApplicants] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const contextMenuRef = useRef(null);
  const gridWrapperRef = useRef(null); // Ref for the grid wrapper
  const navigate = useNavigate();
  const fetchCalled = useRef(false);

  useEffect(() => {
    if (fetchCalled.current) return;
    fetchCalled.current = true;

    setIsLoading(true);

    api.get('/Flights')
      .then((res) => {
        const transformedData = res.data.map((item) => ({
          ...item,
          FlightID: item.flightID,
        }));

        setApplicants(transformedData);
      })
      .catch((err) => console.error('Error fetching applicants:', err))
      .finally(() => setIsLoading(false));
  }, []);

  const handleContextMenuClick = (e) => {
    if (!selectedRowData) return;

    switch (e.itemData.text) {
      case 'Print Passangers':
        navigate("/reports/print_passangers", { state: { selectedIds: selectedIds.length > 0 ? selectedIds : [selectedRowData.ComplainID] } });
        break;
        case 'Delete':
          navigate(`/flights/delete/${selectedRowData.flightID}`);
          break;
        default:
        break;
    }
  };

  const handleContextMenuPreparing = (e) => {
    // Check if the right-click occurred within the grid wrapper
    if (e.row && e.row.data && gridWrapperRef.current?.contains(e.event.target)) {
      setSelectedRowData(e.row.data);
      contextMenuRef.current?.instance.option('target', e.rowElement);
    } else {
      // Cancel the event if the right-click is outside the grid
      e.cancel = true;
    }
  };

  const handleSelectionChange = (e) => {
    const ids = e.selectedRowKeys;
    setSelectedIds(ids);
  };

  const columns = useMemo(
    () => [
      { field: 'airline', caption: 'Airline' },
      { field: 'flightCode', caption: 'Flight Code' },
      { field: 'passportNo', caption: 'Passport#' },
      { field: 'fullName', caption: 'Full Name' },
      { field: 'departDate', caption: 'Depart Date' },
      { field: 'arrivalDate', caption: 'Arrival Date' },
      { field: 'departCity', caption: 'Depart City' },
      { field: 'arrivalCity', caption: 'Arrival City' },
    ],
    []
  );

  return (
    <div className="w-100 h-100 bg-white rounded p-1" ref={gridWrapperRef}> 
    {/* Add ref to the wrapper */}
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        position={{ of: '.dx-datagrid' }}
        visible={isLoading}
        showIndicator={true}
        shading={false}
        showPane={true}
      />

      <DataGrid
        name = "_dxGrid"
        dataSource={applicants}
        showBorders
        rowAlternationEnabled
        allowColumnResizing
        columnAutoWidth       
        wordWrapEnabled 
        focusedRowEnabled
        showColumnLines
        keyExpr="flightID"
        height="100%"
        width="100%"
        onContextMenuPreparing={handleContextMenuPreparing}
        onSelectionChanged={handleSelectionChange}
      >
        <Toolbar style={{ backgroundColor: "black" }}>
            <Item location="before">
              <div className="d-flex align-items-center es-label-title">
                <strong>FLIGHTS</strong> 
                {isLoading ? (
                  <span className="text-primary ms-1">Loading...</span>
                ) : (
                  <>
                    <span className="es-label-title-orange ms-1">[{applicants.length}]</span>                  
                  </>
                )}
              </div>
            </Item>
          <Item name="searchPanel" />          
        </Toolbar>

        <SearchPanel visible highlightCaseSensitive />
        <FilterRow visible />
        <Selection mode="multiple" />
        <Paging defaultPageSize={15} />
        <Pager showPageSizeSelector allowedPageSizes={[15, 25, 50]} showInfo />

        <Column
          caption="..."
          width={50}
          cellRender={(data) => <Link to={`/flights/edit/${data.data.flightID}`}>Edit...</Link>}
        />

        {columns.map(({ field, caption }) => (
          <Column key={field} dataField={field} caption={caption} allowFiltering 
          customizeText={field === 'departDate' || field === 'arrivalDate'
            ? (cellInfo) => {
                if (!cellInfo.value) return '';
                const date = new Date(cellInfo.value);
                return isNaN(date.getTime()) ? cellInfo.value : date.toLocaleDateString('en-GB');
              }
            : undefined}
          />
        ))}

      </DataGrid>

      <ContextMenu
        ref={contextMenuRef}
        onItemClick={handleContextMenuClick}
        target=".dx-datagrid-rowsview" // Apply to grid rows only
        items={[
          { text: 'Print Passangers', icon: 'print' },
          { text: 'Delete', icon: 'delete' }
        ]}
        onShowing={(e) => {
          // Only show our context menu if we have selected row data
          if (!selectedRowData) {
            e.cancel = true;
          }
        }}
        onHidden={() => {
          // Clear selection when menu closes
          setSelectedRowData(null);
        }}
        focusStateEnabled={false}
      />
    </div>
  );
};

export default FlifgtList;